<template>
  <div class="dashboard-card" @click="$emit('click')">
    <div class="card-preview">
      <div
        v-if="previewSlots.length"
        class="preview-grid"
        :class="'preview-grid--' + previewSlots.length"
      >
        <div
          v-for="(slot, index) in previewSlots"
          :key="'plot-' + index"
          class="preview-item"
        >
          <MiniPlotPreview :plot-config="slot" />
        </div>
      </div>
    </div>
    
    <div class="card-content">
      <div class="card-info">
        <h3 class="card-title">{{ dashboard.title }}</h3>
        <div class="card-stats">
          <div class="stat-item">
            <img :src="gridIcon" alt="" width="16" height="16" class="stat-icon-img" />
            <span class="stat-text">{{ dashboard.plotCount }}/{{ dashboard.maxPlots }}</span>
          </div>
          <div class="stat-item" v-if="dashboard.shared">
            <v-icon size="17" class="stat-icon">mdi-earth</v-icon>
            <span class="stat-text">Shared</span>
          </div>
        </div>
      </div>
      <v-menu
        location="bottom end"
        :close-on-content-click="false"
        transition="scale-transition"
        content-class="card-menu-overlay"
        offset="10"
      >
        <template #activator="{ props: menuProps }">
          <button
            class="card-menu-btn"
            v-bind="menuProps"
            @click.stop
            aria-label="Dashboard options"
          >
            <v-icon size="22">mdi-dots-vertical</v-icon>
          </button>
        </template>
        <div class="card-menu-panel">
          <button
            v-if="canEdit"
            type="button"
            class="card-menu-action"
            @click.stop="$emit('edit')"
          >
            <span class="card-menu-icon card-menu-icon--edit">
              <img :src="editIcon" alt="" width="16" height="16" />
            </span>
            <span class="card-menu-label">
              <span class="card-menu-label-title">Edit</span>
              <span class="card-menu-label-sub">Open dashboard</span>
            </span>
          </button>
          <div v-if="canEdit && canStar" class="card-menu-divider" role="separator"></div>
          <button
            v-if="canStar"
            type="button"
            class="card-menu-action"
            @click.stop="$emit('star')"
          >
            <span class="card-menu-icon card-menu-icon--star">
              <img
                :src="dashboard.isStarred ? starFilledIcon : starEmptyIcon"
                alt=""
                width="16"
                height="16"
              />
            </span>
            <span class="card-menu-label">
              <span class="card-menu-label-title">{{ dashboard.isStarred ? 'Unstar' : 'Star' }}</span>
              <span class="card-menu-label-sub">{{ dashboard.isStarred ? 'Remove from starred' : 'Add to starred' }}</span>
            </span>
          </button>
          <div v-if="(canEdit || canStar) && canShare" class="card-menu-divider" role="separator"></div>
          <div v-if="canShare" class="card-menu-share" @click.stop>
            <span class="card-menu-share-label">Visibility</span>
            <div class="tab-div dash-access-toggle card-menu-access-toggle">
              <v-btn-toggle
                :model-value="isPublic"
                class="axis-toggle font-overlay"
                mandatory
                density="compact"
                @update:model-value="handleShareToggle"
              >
                <v-btn :value="false" class="switch-btn" outlined>My Dashboard</v-btn>
                <v-btn :value="true" class="switch-btn" outlined>Shared</v-btn>
              </v-btn-toggle>
            </div>
          </div>
          <div v-if="canDelete" class="card-menu-divider" role="separator"></div>
          <button
            v-if="canDelete"
            type="button"
            class="card-menu-action card-menu-action--delete"
            @click.stop="$emit('delete')"
          >
            <span class="card-menu-icon card-menu-icon--delete">
              <img :src="deleteIcon" alt="" width="16" height="16" />
            </span>
            <span class="card-menu-label">
              <span class="card-menu-label-title">Delete</span>
              <span class="card-menu-label-sub">Remove permanently</span>
            </span>
          </button>
        </div>
      </v-menu>
    </div>
  </div>
</template>

<script>
import MiniPlotPreview from "@/components/dashboard/MiniPlotPreview.vue";

export default {
  name: "DashboardCard",
  components: { MiniPlotPreview },
  props: {
    dashboard: {
      type: Object,
      required: true,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    canStar: {
      type: Boolean,
      default: true,
    },
    canShare: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    isPublic: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click", "star", "edit", "delete", "share-change"],
  data() {
    return {
      starEmptyIcon: require("@/assets/Images/Dashboard/star.svg"),
      starFilledIcon: require("@/assets/Svg/Dashboard/small-filled-star.svg"),
      gridIcon: require("@/assets/Svg/Icons/view-grid.svg"),
      editIcon: require("@/assets/Svg/Icons/edit-2.svg"),
      deleteIcon: require("@/assets/Svg/Icons/trash.svg"),
    };
  },
  computed: {
    /** Up to 4 `{ data, chartData }` entries — only real plots (no placeholder cells). */
    previewSlots() {
      const plots = this.dashboard.previewPlotConfigs || [];
      return plots.filter(Boolean).slice(0, 4);
    },
  },
  methods: {
    handleShareToggle(value) {
      this.$emit("share-change", value);
    },
  },
};
</script>

<!-- Teleported v-menu content lives outside the card; keep these global -->
